import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../app/useAuth'
import { BrandMark } from '../../components/BrandMark'
import { Button, Field, Input, Pill, Spinner } from '../../components/ui'
import { api } from '../../lib/api/client'
import './mock-checkout.css'

type Outcome = 'success' | 'failed' | 'cancelled'

export function MockCheckout() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const { user } = useAuth()
  const fallbackReturnTo = user?.roles.includes('admin') ? '/schools' : '/settings?tab=ai'
  const [cardholder, setCardholder] = useState('Test School Admin')
  const [cardNumber, setCardNumber] = useState('4111 1111 1111 1111')
  const [result, setResult] = useState<Outcome | null>(null)

  const session = useQuery({
    queryKey: ['mock-billing', token],
    queryFn: () => api.mockBillingSession(token),
    enabled: Boolean(token),
    retry: false,
  })
  const complete = useMutation({
    mutationFn: (outcome: Outcome) => api.completeMockBilling(token, outcome),
    onSuccess: (_data, outcome) => setResult(outcome),
  })

  if (!token) return <MockError message="This mock checkout link is missing its token." returnTo={fallbackReturnTo} />
  if (session.isLoading) return <div className="tc-mock-loading"><Spinner label="Loading checkout" /></div>
  if (session.isError || !session.data) {
    return <MockError message={session.error instanceof Error ? session.error.message : 'Checkout could not be loaded.'} returnTo={fallbackReturnTo} />
  }

  const data = complete.data ?? session.data
  const amount = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: data.currency,
  }).format(data.amount_paise / 100)
  const returnTo = user?.roles.includes('admin')
    ? `/schools/${data.school_id}/settings?tab=ai`
    : '/settings?tab=ai'

  if (result) {
    const copy = {
      success: ['Payment succeeded', 'The mock monthly subscription is now active.'],
      failed: ['Payment failed', 'No subscription access was granted. You can retry from billing settings.'],
      cancelled: ['Checkout cancelled', 'No payment was made and no subscription access was granted.'],
    }[result]
    return (
      <div className="tc-mock-page">
        <section className="tc-mock-card tc-mock-result">
          <Pill tone={result === 'success' ? 'success' : result === 'failed' ? 'danger' : 'muted'}>
            Development simulator
          </Pill>
          <h1>{copy[0]}</h1>
          <p>{copy[1]}</p>
          <Link className="tc-mock-link" to={returnTo}>Return to AI &amp; billing</Link>
        </section>
      </div>
    )
  }

  return (
    <div className="tc-mock-page">
      <section className="tc-mock-card">
        <header className="tc-mock-header">
          <BrandMark size={34} />
          <div><strong>TeachCraftAI</strong><span>Mock checkout</span></div>
          <Pill tone="warning">Development only</Pill>
        </header>
        <div className="tc-mock-summary">
          <div><span>School</span><strong>{data.school_name}</strong></div>
          <div><span>Plan</span><strong>Platform AI · Monthly</strong></div>
          <div className="tc-mock-price"><span>Due today</span><strong>{amount}</strong></div>
        </div>
        <div className="tc-mock-notice" role="note">
          This is a local simulator. Do not enter real card or bank details.
        </div>
        <form
          className="tc-mock-form"
          onSubmit={(event) => {
            event.preventDefault()
            complete.mutate('success')
          }}
        >
          <Field label="Name on card">
            <Input required value={cardholder} onChange={(event) => setCardholder(event.target.value)} />
          </Field>
          <Field label="Test card number" hint="Any test-looking number is accepted by this simulator.">
            <Input required inputMode="numeric" value={cardNumber} onChange={(event) => setCardNumber(event.target.value)} />
          </Field>
          <div className="tc-mock-grid">
            <Field label="Expiry"><Input required defaultValue="12/30" /></Field>
            <Field label="CVV"><Input required inputMode="numeric" defaultValue="123" /></Field>
          </div>
          {complete.isError && <p className="tc-mock-error" role="alert">{complete.error.message}</p>}
          <Button type="submit" variant="primary" disabled={complete.isPending}>
            {complete.isPending ? 'Processing…' : `Pay ${amount}`}
          </Button>
          <div className="tc-mock-actions">
            <Button disabled={complete.isPending} onClick={() => complete.mutate('failed')}>Simulate failure</Button>
            <Button variant="ghost" disabled={complete.isPending} onClick={() => complete.mutate('cancelled')}>Cancel checkout</Button>
          </div>
        </form>
      </section>
    </div>
  )
}

function MockError({ message, returnTo }: { message: string; returnTo: string }) {
  return (
    <div className="tc-mock-page">
      <section className="tc-mock-card tc-mock-result" role="alert">
        <Pill tone="danger">Mock checkout unavailable</Pill>
        <h1>Checkout could not be opened</h1>
        <p>{message}</p>
        <Link className="tc-mock-link" to={returnTo}>Return to AI &amp; billing</Link>
      </section>
    </div>
  )
}
